import React from "react";
import { UserCheck, Eye, MessageCircle, Clock } from "lucide-react";
import MarketingPage, { MarketingSection, Pillar } from "@/apps/rider/components/MarketingPage";

export default function Safety() {
  return (
    <MarketingPage
      eyebrow="Safety & trust"
      title="Built around the same trip, every day."
      lede="Link is designed for repeat rides on a known route — which means safety isn't a feature, it's the operating model. Every driver is verified, every ride is transparent, and a real human picks up when you call."
      back="/"
      backLabel="Home"
      testId="safety-page"
      cta={{ to: "/support/contact", label: "Talk to our safety team" }}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Pillar Icon={UserCheck} title="Verified drivers" testId="safety-pillar-drivers">
          Every Link driver clears a multi-state background check, motor-vehicle records check, and an in-person vehicle inspection before their first ride.
        </Pillar>
        <Pillar Icon={Eye} title="Transparent rides" testId="safety-pillar-transparency">
          You see your driver's name, photo, vehicle, and license plate before pickup. Live ETA + arriving-now notifications keep you ahead of the door knock.
        </Pillar>
        <Pillar Icon={MessageCircle} title="Real human support" testId="safety-pillar-support">
          Our support inbox is monitored every hour drivers are on the road. You'll never be routed to a chatbot for an active-ride concern.
        </Pillar>
        <Pillar Icon={Clock} title="On-time accountability" testId="safety-pillar-ontime">
          If a Link driver misses a pickup window by 30 minutes, that ride is automatically refunded — no form, no email, no asking.
        </Pillar>
      </div>

      <MarketingSection eyebrow="Same driver, same trip" title="Familiarity is a safety feature." testId="safety-driver-pool">
        <p>
          Because Link runs the same route on the same days, our driver pool stabilizes around you. Most riders see two or three drivers across a typical month — not the sixty-different-strangers cycle of a rideshare. That repeat exposure builds real trust, both ways.
        </p>
      </MarketingSection>

      <MarketingSection eyebrow="On every ride" title="What you can count on." testId="safety-checklist">
        <ul className="list-disc pl-5 space-y-2">
          <li><strong className="text-[#0A1128]">Driver introduction up front.</strong> Photo, vehicle, plate, ETA — all visible before pickup.</li>
          <li><strong className="text-[#0A1128]">Live arrival alerts.</strong> A 5-minute pre-pickup ping plus an "arriving now" alert.</li>
          <li><strong className="text-[#0A1128]">Verified-only pool.</strong> Background check, MVR, in-person inspection, periodic re-checks.</li>
          <li><strong className="text-[#0A1128]">Two-way feedback.</strong> Riders rate drivers; drivers can decline future trips with patterns of unsafe pickup behavior.</li>
          <li><strong className="text-[#0A1128]">Insurance.</strong> Drivers carry commercial coverage; we layer supplemental coverage during active Link rides.</li>
        </ul>
      </MarketingSection>

      <MarketingSection eyebrow="If something goes wrong" title="One tap to a human." testId="safety-incidents">
        <p>
          Use <a href="/support/report" className="text-[#ed6500] underline-offset-4 hover:underline">Report an issue</a> from the Account screen for ride or payment concerns. For urgent safety situations during an active ride, call 911 first — then let us know, and we'll handle the operations side immediately.
        </p>
      </MarketingSection>

      <div className="bg-[#0A1128] text-[#F9F7F4] rounded-3xl p-6 sm:p-8 mt-4 relative overflow-hidden" data-testid="safety-promise">
        <div className="absolute -right-14 -top-14 w-56 h-56 bg-[#ed6500]/12 rounded-full blur-3xl" aria-hidden="true" />
        <div className="text-[10px] tracking-[0.24em] uppercase font-semibold text-[#ed6500] mb-2">
          Our promise
        </div>
        <h2 className="font-serif text-[26px] sm:text-[30px] leading-tight">
          A safer commute is a quieter one.
        </h2>
        <p className="text-[14.5px] text-[#F9F7F4]/75 mt-4 leading-relaxed">
          The best safety record we can ship is the one where you don't think about safety at all — where pickup happens on time, the route runs as planned, and you're at your desk five minutes early. That's what we optimize for.
        </p>
      </div>

      {/* CTA card handled by MarketingPage shell below */}
    </MarketingPage>
  );
}
